import type { GatewayModelInfo } from '@/app/sdk';

/** Best-effort slugs (order preserved); reconciled against live catalogue on load. */
export const ASSISTANT_DEFAULT_PINNED_MODEL_IDS: readonly string[] = [
  'composer-2.5',
  'claude-sonnet-4-6',
  'gpt-5.3-codex',
  'gpt-5.5',
  'claude-opus-4-8',
  'claude-opus-4-6',
];

function normaliseDisplayKey(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

function findModelIdByHint(catalogue: GatewayModelInfo[], hint: string): string | undefined {
  const hintKey = normaliseDisplayKey(hint);
  if (!hintKey) {
    return undefined;
  }

  const byDisplay = catalogue.find(model => normaliseDisplayKey(model.displayName) === hintKey);
  if (byDisplay) {
    return byDisplay.id;
  }

  const hintTokens = hintKey.split(/\s+/).filter(Boolean);
  const byContains = catalogue.find(model => {
    const haystack = `${normaliseDisplayKey(model.displayName)} ${normaliseDisplayKey(model.id)}`;
    return hintTokens.every(token => haystack.includes(token));
  });
  return byContains?.id;
}

/** Resolves editor recommended model ids against the live catalogue (preserves order). */
export function resolveRecommendedPinnedIds(catalogue: GatewayModelInfo[]): string[] {
  return resolveDefaultPinnedIds(catalogue);
}

function resolveDefaultPinnedIds(catalogue: GatewayModelInfo[]): string[] {
  const resolved: string[] = [];

  for (const hint of ASSISTANT_DEFAULT_PINNED_MODEL_IDS) {
    const exactMatch = catalogue.find(model => model.id === hint)?.id;
    const resolvedId = exactMatch ?? findModelIdByHint(catalogue, hint);
    if (resolvedId && !resolved.includes(resolvedId)) {
      resolved.push(resolvedId);
    }
  }

  return resolved;
}

/**
 * Keeps user pin order and drops unknown ids.
 * Seeds recommended defaults only when pins were never provided (`undefined`).
 */
export function normalisePinnedModelIds(
  catalogue: GatewayModelInfo[],
  storedIds?: string[],
): string[] {
  const catalogueIds = new Set(catalogue.map(model => model.id));

  if (storedIds === undefined) {
    return resolveDefaultPinnedIds(catalogue);
  }

  const seen = new Set<string>();
  const stored: string[] = [];
  for (const id of storedIds) {
    const trimmed = id.trim();
    if (!trimmed || !catalogueIds.has(trimmed) || seen.has(trimmed)) {
      continue;
    }
    seen.add(trimmed);
    stored.push(trimmed);
  }

  return stored;
}

export function isModelPinned(pinnedModelIds: string[], modelId: string): boolean {
  return pinnedModelIds.includes(modelId);
}
